import React, { useEffect, useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ArrowLeft, Shield, Zap, Crown, Plus, BarChart3, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { handleGet, handlePut } from "../../controllers/controller.all";

const PLANS = {
  STANDARD: {
    label: "Standard",
    color: "indigo",
    icon: Shield,
    description: "Pour les petites et moyennes écoles",
    limits: { ai_student: 100, ai_teacher: 200, sms: 300, mail: 200 },
  },
  PREMIUM: {
    label: "Premium",
    color: "violet",
    icon: Zap,
    description: "Pour les écoles en croissance",
    limits: { ai_student: 500, ai_teacher: 1000, sms: 1500, mail: 1000 },
  },
  VIP: {
    label: "VIP",
    color: "amber",
    icon: Crown,
    description: "Accès illimité à toutes les fonctionnalités",
    limits: { ai_student: "∞", ai_teacher: "∞", sms: "∞", mail: "∞" },
  },
};

const RESOURCE_LABELS = {
  ai_student: { label: "IA — Élèves", unit: "req/mois" },
  ai_teacher: { label: "IA — Profs", unit: "req/mois" },
  sms: { label: "SMS", unit: "SMS/mois" },
  mail: { label: "Emails", unit: "emails/mois" },
};

function PlanBadge({ plan }) {
  const cfg = PLANS[plan] || PLANS.STANDARD;
  const colors = {
    indigo: "bg-indigo-100 text-indigo-700 ring-indigo-200",
    violet: "bg-violet-100 text-violet-700 ring-violet-200",
    amber: "bg-amber-100 text-amber-700 ring-amber-200",
  };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ring-1 ${colors[cfg.color]}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}

function QuotaBar({ label, consumed, total, extra, unit }) {
  const isUnlimited = total === -1;
  const realTotal = isUnlimited ? null : total + (extra || 0);
  const pct = realTotal && realTotal > 0 ? Math.min((consumed / realTotal) * 100, 100) : 0;
  const color = pct >= 80 ? "bg-red-500" : pct >= 60 ? "bg-amber-500" : "bg-emerald-500";

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between items-center text-xs">
        <span className="text-gray-600 font-medium">{label}</span>
        {isUnlimited ? (
          <span className="text-emerald-600 font-semibold">Illimité ∞</span>
        ) : (
          <span className="text-gray-500">{consumed} / {realTotal} {unit}</span>
        )}
      </div>
      {!isUnlimited && (
        <>
          <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${pct}%` }} />
          </div>
          {extra > 0 && (
            <p className="text-xs text-violet-600">+{extra} unités supplémentaires</p>
          )}
        </>
      )}
    </div>
  );
}

export default function SchoolPlanView() {
  const { state } = useLocation();
  const id = state?.id;
  const navigate = useNavigate();
  const user = useSelector((state) => state.userReducer?.user);
  const dispatch = useDispatch();
  const token = user?.access_token || "";

  const [ecole, setEcole] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [addQuota, setAddQuota] = useState({ resource: "sms", amount: "" });
  const [toast, setToast] = useState(null);

  const showToast = useCallback((msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const fetchData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    
    await handleGet(`/ecole/${id}`, (res) => {
      if (res?.ecole) {
        setEcole(prev => ({ ...res.ecole, plan: prev?.plan && silent ? prev.plan : (res.ecole.plan || "STANDARD") }));
        if (!silent) setSelectedPlan(res.ecole.plan || "STANDARD");
      }
    }, token);
    
    await handleGet(`/school-plan/${id}/stats`, (res) => {
      if (res) setStats(res);
    }, token);
    if (!silent) setLoading(false);
  }, [id, token]);

  useEffect(() => { if (token && id) fetchData(); }, [fetchData, token, id]);

  const handlePlanSave = async () => {
    if (!selectedPlan || selectedPlan === ecole?.plan) return;
    setSaving(true);
    await handlePut(`/school-plan/${id}`, { plan: selectedPlan }, (res) => {
      if (res?.state === "success") {
        showToast(`Plan mis à jour en ${selectedPlan} ✓`);
        // Optimistic update so it displays immediately
        setEcole(prev => ({ ...prev, plan: selectedPlan }));
        
        // Sync Redux so the plan badge updates everywhere without re-login
        if (user?.ecole && (user.ecole._id === id || user.ecole === id)) {
          dispatch({ type: "user-update", value: { ...user, ecole: { ...user.ecole, plan: selectedPlan } } });
        }
        
        // Fetch data silently to update stats without full page loading
        fetchData(true);
      } else {
        showToast("Erreur lors de la mise à jour", "error");
      }
    }, token);
    setSaving(false);
  };

  const handleAddQuota = async () => {
    const amount = parseInt(addQuota.amount);
    if (!amount || amount <= 0) return;
    setSaving(true);
    await handlePut(`/school-plan/${id}/add-quota`, { resource: addQuota.resource, amount }, (res) => {
      if (res?.state === "success") {
        showToast(`${amount} unités de ${addQuota.resource} ajoutées ✓`);
        setAddQuota(a => ({ ...a, amount: "" }));
        fetchData(true);
      } else {
        showToast("Erreur lors de l'ajout", "error");
      }
    }, token);
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64 text-gray-400">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  );

  return (
    <div className="flex flex-col gap-6 max-w-4xl mx-auto">

      {/* Toast */}
      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-medium text-white transition
          ${toast.type === "error" ? "bg-red-500" : "bg-emerald-500"}`}>
          {toast.type === "error" ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-900">{ecole?.nom}</h1>
          <div className="flex items-center gap-2 mt-1">
            <PlanBadge plan={ecole?.plan || "STANDARD"} />
            <span className="text-xs text-gray-400">{ecole?.ville}, {ecole?.pays}</span>
          </div>
        </div>
        <button onClick={fetchData} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 transition" title="Actualiser">
          <RefreshCw className="h-4 w-4" />
        </button>
      </div>

      {/* Plan Selection */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-indigo-500" /> Changer le plan
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          {Object.entries(PLANS).map(([key, cfg]) => {
            const Icon = cfg.icon;
            const selected = selectedPlan === key;
            const borderColors = { indigo: "border-indigo-500 ring-indigo-200", violet: "border-violet-500 ring-violet-200", amber: "border-amber-500 ring-amber-200" };
            const bgColors = { indigo: "bg-indigo-50", violet: "bg-violet-50", amber: "bg-amber-50" };
            const textColors = { indigo: "text-indigo-700", violet: "text-violet-700", amber: "text-amber-700" };
            return (
              <button key={key} onClick={() => setSelectedPlan(key)}
                className={`relative p-4 rounded-xl border-2 text-left transition ${
                  selected
                    ? `${borderColors[cfg.color]} ring-1 ${bgColors[cfg.color]}`
                    : "border-gray-200 hover:border-gray-300 bg-white"
                }`}>
                <div className={`flex items-center gap-2 mb-2 ${selected ? textColors[cfg.color] : "text-gray-600"}`}>
                  <Icon className="h-5 w-5" />
                  <span className="font-semibold">{cfg.label}</span>
                </div>
                <p className="text-xs text-gray-500 mb-3">{cfg.description}</p>
                <ul className="space-y-0.5 text-xs text-gray-600">
                  {Object.entries(cfg.limits).map(([r, v]) => (
                    <li key={r} className="flex justify-between">
                      <span>{RESOURCE_LABELS[r]?.label}</span>
                      <span className={`font-medium ${selected ? textColors[cfg.color] : ""}`}>{v === "∞" ? "Illimité" : `${v} ${RESOURCE_LABELS[r]?.unit}`}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
        <button
          onClick={handlePlanSave}
          disabled={saving || selectedPlan === ecole?.plan}
          className="w-full py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition">
          {saving ? "Enregistrement..." : selectedPlan === ecole?.plan ? "Plan déjà actif" : `Passer au plan ${PLANS[selectedPlan]?.label}`}
        </button>
      </div>

      {/* Consumption Stats */}
      {stats?.resources && (
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
          <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-violet-500" /> Consommation du mois — {new Date().toLocaleString("fr-FR", { month: "long", year: "numeric" })}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {Object.entries(stats.resources).map(([resource, data]) => (
              <QuotaBar
                key={resource}
                label={RESOURCE_LABELS[resource]?.label || resource}
                consumed={data.consumed}
                total={data.quota_base}
                extra={data.extra}
                unit={RESOURCE_LABELS[resource]?.unit}
              />
            ))}
          </div>
        </div>
      )}

      {/* Add Extra Quota */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5 text-emerald-500" /> Ajouter un pack supplémentaire
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Ajoutez des unités supplémentaires manuellement. Ces unités s'ajoutent au quota de base du plan.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <select
            value={addQuota.resource}
            onChange={e => setAddQuota(a => ({ ...a, resource: e.target.value }))}
            className="flex-1 rounded-lg border border-gray-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500">
            {Object.entries(RESOURCE_LABELS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
          <input
            type="number"
            min="1"
            value={addQuota.amount}
            onChange={e => setAddQuota(a => ({ ...a, amount: e.target.value }))}
            placeholder="Nombre d'unités"
            className="flex-1 rounded-lg border border-gray-300 text-sm px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            onClick={handleAddQuota}
            disabled={saving || !addQuota.amount || parseInt(addQuota.amount) <= 0}
            className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition whitespace-nowrap">
            {saving ? "..." : "Ajouter"}
          </button>
        </div>
      </div>
    </div>
  );
}
